/**
 * Record collection catalogue: the field schemas behind the Inventory and
 * Registries areas (PLAN follow-up, increment 4). Each collection is a
 * multi-step wizard form; records are stored generically in `org_records`
 * with the answers in a JSON `data` column, validated server-side against
 * these definitions (apps/worker/src/lib/records.ts).
 *
 * Fields marked `column: true` become the columns of list views and of the
 * point-in-time snapshot tables that can be imported into assessment
 * responses ("dynamic import", audited as `response.snapshot_imported`).
 */

export type CollectionGroup = 'inventory' | 'registry';

export type CollectionFieldType =
  | 'text'
  | 'textarea'
  | 'email'
  | 'url'
  | 'number'
  | 'date'
  | 'select'
  | 'multiselect'
  | 'boolean'
  | 'member'
  /** Reference to another record: the value is a single `rec_…` id ('' = none). */
  | 'record';

export interface CollectionField {
  key: string;
  label: string;
  type: CollectionFieldType;
  required?: boolean;
  /** Choices for select/multiselect fields. */
  options?: readonly string[];
  /**
   * Collections a `record` field may reference (required for that type). The
   * referenced record must belong to the same organisation and not be archived.
   */
  refCollections?: readonly string[];
  hint?: string;
  /** Show as a column in list views and snapshot tables. */
  column?: boolean;
}

export interface CollectionStep {
  key: string;
  title: string;
  fields: CollectionField[];
}

export interface CollectionDef {
  key: string;
  group: CollectionGroup;
  /** Plural display name, e.g. "Suppliers", "Risk Registry". */
  name: string;
  /** Singular noun for buttons/dialogs, e.g. "supplier". */
  singular: string;
  description: string;
  /** Records can be assigned to org members (software access, hardware issue). */
  assignable?: boolean;
  /** What an assignment represents, e.g. "access" or "device". */
  assignmentNoun?: string;
  steps: CollectionStep[];
}

// ----------------------------------------------------------- shared options

export const COUNTRIES: readonly string[] = [
  'United Kingdom',
  'Ireland',
  'United States',
  'Afghanistan', 'Albania', 'Algeria', 'Argentina', 'Armenia', 'Australia',
  'Austria', 'Azerbaijan', 'Bahrain', 'Bangladesh', 'Belarus', 'Belgium',
  'Bolivia', 'Bosnia and Herzegovina', 'Botswana', 'Brazil', 'Bulgaria',
  'Cambodia', 'Cameroon', 'Canada', 'Chile', 'China', 'Colombia',
  'Costa Rica', 'Croatia', 'Cyprus', 'Czechia', 'Denmark',
  'Dominican Republic', 'Ecuador', 'Egypt', 'Estonia', 'Ethiopia', 'Finland',
  'France', 'Georgia', 'Germany', 'Ghana', 'Greece', 'Guatemala', 'Honduras',
  'Hong Kong', 'Hungary', 'Iceland', 'India', 'Indonesia', 'Iraq', 'Israel',
  'Italy', 'Jamaica', 'Japan', 'Jordan', 'Kazakhstan', 'Kenya', 'Kuwait',
  'Latvia', 'Lebanon', 'Liechtenstein', 'Lithuania', 'Luxembourg', 'Malaysia',
  'Malta', 'Mauritius', 'Mexico', 'Moldova', 'Monaco', 'Mongolia',
  'Montenegro', 'Morocco', 'Mozambique', 'Myanmar', 'Nepal', 'Netherlands',
  'New Zealand', 'Nicaragua', 'Nigeria', 'North Macedonia', 'Norway', 'Oman',
  'Pakistan', 'Panama', 'Paraguay', 'Peru', 'Philippines', 'Poland',
  'Portugal', 'Qatar', 'Romania', 'Rwanda', 'Saudi Arabia', 'Senegal',
  'Serbia', 'Singapore', 'Slovakia', 'Slovenia', 'South Africa',
  'South Korea', 'Spain', 'Sri Lanka', 'Sweden', 'Switzerland', 'Taiwan',
  'Tanzania', 'Thailand', 'Tunisia', 'Turkey', 'Uganda', 'Ukraine',
  'United Arab Emirates', 'Uruguay', 'Uzbekistan', 'Venezuela', 'Vietnam',
  'Zambia', 'Zimbabwe', 'Other',
];

const DATA_SUBJECTS = [
  'Employees', 'Contractors', 'Job applicants', 'Customers',
  'Prospective customers', 'Suppliers', 'Patients', 'Children', 'Students',
  'Website visitors', 'Members of the public',
] as const;

const DATA_CATEGORIES = [
  'Contact details', 'Financial data', 'HR & payroll data',
  'Identity documents', 'Health data', 'Special category data',
  'Criminal records data', 'Usage & analytics data', 'Credentials',
  'Location data', 'Images & CCTV', 'None',
] as const;

const TRANSFER_SAFEGUARDS = [
  'Not applicable', 'UK adequacy regulations',
  'Standard Contractual Clauses (SCCs)',
  'International Data Transfer Agreement (IDTA)',
  'Binding Corporate Rules', 'UK–US Data Bridge', 'Other',
] as const;

const LAWFUL_BASES = [
  'Consent', 'Contract', 'Legal obligation', 'Vital interests', 'Public task',
  'Legitimate interests',
] as const;

const IMPACT_LEVELS = ['Low', 'Medium', 'High', 'Critical'] as const;

const LIKERT_LIKELIHOOD = [
  '1 - Rare', '2 - Unlikely', '3 - Possible', '4 - Likely',
  '5 - Almost certain',
] as const;

const LIKERT_IMPACT = [
  '1 - Negligible', '2 - Minor', '3 - Moderate', '4 - Major', '5 - Severe',
] as const;

// -------------------------------------------------------------- collections

export const COLLECTIONS: readonly CollectionDef[] = [
  // ------------------------------------------------------------- inventory
  {
    key: 'products',
    group: 'inventory',
    name: 'Company Products',
    singular: 'product',
    description: 'Products and services your company builds, sells or operates.',
    steps: [
      {
        key: 'product',
        title: 'Product Info',
        fields: [
          { key: 'name', label: 'Product name', type: 'text', required: true, column: true },
          {
            key: 'type', label: 'Product type', type: 'select', column: true,
            options: ['Software / SaaS', 'Hardware', 'Managed service', 'Consultancy', 'Content / media', 'Other'],
          },
          { key: 'owner', label: 'Product owner', type: 'member', column: true },
          { key: 'description', label: 'Description', type: 'textarea' },
          {
            key: 'lifecycle', label: 'Lifecycle stage', type: 'select', column: true,
            options: ['Concept', 'In development', 'Live', 'Deprecated', 'Retired'],
          },
        ],
      },
      {
        key: 'technical',
        title: 'Technical & Data',
        fields: [
          {
            key: 'hosting', label: 'Hosting', type: 'multiselect',
            options: ['Cloudflare', 'AWS', 'Azure', 'Google Cloud', 'On-premises', 'Third-party managed', 'Not applicable'],
          },
          { key: 'url', label: 'Product URL', type: 'url' },
          { key: 'repositories', label: 'Source repositories', type: 'text', hint: 'Where the source code lives, if applicable' },
          { key: 'containsPersonalData', label: 'Processes personal data?', type: 'boolean', column: true },
          { key: 'securityContact', label: 'Security contact', type: 'text', hint: 'Who receives vulnerability reports for this product' },
        ],
      },
    ],
  },
  {
    key: 'information-assets',
    group: 'inventory',
    name: 'Information Assets',
    singular: 'information asset',
    description:
      'Register of information and associated assets with owner, classification (confidentiality, integrity, availability), access and risk links — the inventory ISO 27001 A.5.9 and A.5.12 expect.',
    steps: [
      {
        key: 'asset',
        title: 'Asset',
        fields: [
          { key: 'assetNumber', label: 'Asset number', type: 'text', hint: 'Your reference number, e.g. 12' },
          { key: 'name', label: 'Information asset', type: 'text', required: true, column: true },
          {
            key: 'category', label: 'Asset category', type: 'select', required: true, column: true,
            options: ['Hardware', 'Software', 'Information', 'Site', 'Personnel', 'Supplier', 'Service', 'Paper records', 'Other'],
          },
          { key: 'description', label: 'Description / use', type: 'textarea' },
          { key: 'location', label: 'Location', type: 'text', hint: 'Where it is held or hosted, e.g. On persons, Office, London (AWS), Online' },
          { key: 'owner', label: 'Asset owner', type: 'member', column: true },
          { key: 'admin', label: 'Administrator(s) (where applicable)', type: 'text' },
          {
            key: 'linkedRecord', label: 'Linked inventory record', type: 'record',
            refCollections: ['software', 'hardware', 'suppliers', 'websites'],
            hint: 'The software, device, supplier or website this asset relates to, if listed',
          },
          { key: 'personalData', label: 'Holds personal data?', type: 'boolean' },
          { key: 'specialCategory', label: 'Holds special category personal data?', type: 'boolean' },
        ],
      },
      {
        key: 'classification',
        title: 'Classification',
        fields: [
          {
            key: 'confidentiality', label: 'Confidentiality', type: 'select', column: true,
            options: ['Public', 'Internal use', 'Confidential', 'Strictly confidential', 'N/A'],
          },
          { key: 'confidentialityJustification', label: 'Confidentiality justification', type: 'textarea' },
          {
            key: 'integrity', label: 'Integrity', type: 'select', column: true,
            options: ['Low', 'Moderate', 'High', 'N/A'],
          },
          { key: 'integrityJustification', label: 'Integrity justification', type: 'textarea' },
          {
            key: 'availability', label: 'Availability', type: 'select', column: true,
            options: ['Low', 'Moderate', 'High', 'N/A'],
          },
          { key: 'availabilityJustification', label: 'Availability justification', type: 'textarea' },
          { key: 'riskLink', label: 'Risk register link', type: 'record', refCollections: ['risks'] },
        ],
      },
      {
        key: 'access',
        title: 'Access & review',
        fields: [
          { key: 'mfa', label: 'MFA enforced?', type: 'boolean' },
          { key: 'sso', label: 'Single sign-on?', type: 'boolean' },
          { key: 'ssoProvider', label: 'SSO provider', type: 'text', hint: 'e.g. Microsoft Entra ID' },
          { key: 'accessInternal', label: 'Who has access (internal)?', type: 'text', hint: 'Teams or roles, e.g. Development, Management, All staff' },
          { key: 'accessExternal', label: 'Who has access (external)?', type: 'text' },
          { key: 'reviewDate', label: 'Next review date', type: 'date' },
        ],
      },
    ],
  },
  {
    key: 'software',
    group: 'inventory',
    name: 'Software',
    singular: 'software item',
    description: 'Software and cloud services used by your organisation.',
    assignable: true,
    assignmentNoun: 'access',
    steps: [
      {
        key: 'software',
        title: 'Software Info',
        fields: [
          { key: 'name', label: 'Software name', type: 'text', required: true, column: true },
          { key: 'vendor', label: 'Vendor', type: 'text', column: true },
          {
            key: 'category', label: 'Category', type: 'select', column: true,
            options: ['Operating system', 'Productivity & office', 'Development', 'Security', 'Finance', 'HR', 'CRM & sales', 'Communication', 'Design', 'Infrastructure & cloud', 'Other'],
          },
          { key: 'owner', label: 'Business owner', type: 'member' },
          { key: 'businessCritical', label: 'Business critical', type: 'boolean' },
        ],
      },
      {
        key: 'licensing',
        title: 'Licensing & Versions',
        fields: [
          {
            key: 'licenceType', label: 'Licence type', type: 'select',
            options: ['Subscription (SaaS)', 'Perpetual licence', 'Open source', 'Freeware', 'Trial'],
          },
          { key: 'seats', label: 'Licensed seats', type: 'number' },
          { key: 'renewalDate', label: 'Renewal date', type: 'date' },
          { key: 'version', label: 'Version in use', type: 'text', column: true },
          { key: 'supported', label: 'Version still vendor-supported?', type: 'boolean', column: true, hint: 'Unsupported software fails Cyber Essentials' },
          { key: 'autoUpdates', label: 'Automatic updates enabled?', type: 'boolean' },
        ],
      },
      {
        key: 'security',
        title: 'Security',
        fields: [
          {
            key: 'authMethod', label: 'Authentication method', type: 'select',
            options: ['SSO (SAML / OIDC)', 'Password + MFA', 'Password only', 'API key', 'Local account'],
          },
          { key: 'mfaEnforced', label: 'MFA enforced?', type: 'boolean' },
          {
            key: 'dataStored', label: 'Data stored in this software', type: 'multiselect',
            options: ['None', 'Personal data', 'Special category data', 'Financial data', 'Confidential business data', 'Credentials'],
          },
          { key: 'adminUrl', label: 'Admin console URL', type: 'url' },
        ],
      },
      {
        key: 'cloud',
        title: 'Cloud service & data',
        fields: [
          { key: 'isCloudService', label: 'Cloud service?', type: 'boolean', column: true, hint: 'SaaS/IaaS/PaaS you use, e.g. AWS, Azure, Microsoft 365' },
          {
            key: 'dataLocation', label: 'Where is the data located?', type: 'multiselect',
            options: ['United Kingdom', 'EEA', 'United States', 'Global / multi-region', 'Other third country'],
          },
          { key: 'dataLocationDetail', label: 'Data location detail', type: 'text', hint: 'Regions or data centres, e.g. AWS eu-west-2 (London), Azure UK South' },
          { key: 'accessInternal', label: 'Who has access (internal)?', type: 'text' },
          { key: 'accessExternal', label: 'Who has access (external)?', type: 'text' },
          { key: 'dpia', label: 'DPIA', type: 'record', refCollections: ['dpias'], hint: 'The DPIA (or DPIA screening) covering this service' },
        ],
      },
    ],
  },
  {
    key: 'hardware',
    group: 'inventory',
    name: 'Hardware',
    singular: 'device',
    description: 'Computers, tablets, phones and other devices — the device inventory standards like Cyber Essentials ask for.',
    assignable: true,
    assignmentNoun: 'device',
    steps: [
      {
        key: 'device',
        title: 'Device Info',
        fields: [
          { key: 'name', label: 'Device name / asset tag', type: 'text', required: true, column: true },
          {
            key: 'type', label: 'Device type', type: 'select', required: true, column: true,
            options: ['Laptop', 'Desktop', 'Server', 'Mobile phone', 'Tablet', 'Network device', 'Printer', 'IoT device', 'Removable media', 'Other'],
          },
          { key: 'make', label: 'Make', type: 'text', column: true },
          { key: 'model', label: 'Model', type: 'text', column: true },
          { key: 'serialNumber', label: 'Serial number', type: 'text' },
          {
            key: 'ownership', label: 'Ownership', type: 'select',
            options: ['Company owned', 'Employee owned (BYOD)', 'Leased'],
          },
        ],
      },
      {
        key: 'build',
        title: 'Operating System & Security',
        fields: [
          {
            key: 'os', label: 'Operating system', type: 'select', column: true,
            options: ['Windows 11', 'Windows 10', 'Windows Server', 'macOS', 'iOS', 'iPadOS', 'Android', 'ChromeOS', 'Linux', 'Network firmware', 'Other'],
          },
          { key: 'osEdition', label: 'Edition / distribution', type: 'text', hint: 'e.g. Pro, Enterprise, Ubuntu 24.04 LTS' },
          { key: 'osVersion', label: 'OS version', type: 'text', column: true },
          { key: 'autoUpdates', label: 'Automatic updates enabled?', type: 'boolean' },
          {
            key: 'encryption', label: 'Disk encryption', type: 'select', column: true,
            options: ['Full disk encryption', 'Partial', 'None', 'Unknown'],
          },
          { key: 'firewallEnabled', label: 'Firewall enabled?', type: 'boolean' },
          {
            key: 'malwareProtection', label: 'Malware protection', type: 'select',
            options: ['Built-in (Defender / XProtect)', 'Third-party EDR / AV', 'MDM enforced', 'None'],
          },
        ],
      },
      {
        key: 'lifecycle',
        title: 'Lifecycle',
        fields: [
          { key: 'purchaseDate', label: 'Purchase date', type: 'date' },
          { key: 'warrantyExpiry', label: 'Warranty expiry', type: 'date' },
          { key: 'supportEnd', label: 'OS support end date', type: 'date', hint: 'When the installed OS stops receiving security updates' },
          { key: 'location', label: 'Usual location', type: 'text' },
        ],
      },
    ],
  },
  {
    key: 'suppliers',
    group: 'inventory',
    name: 'Suppliers',
    singular: 'supplier',
    description: 'Third parties you buy services from, with the data protection detail GDPR and ISO 27001 expect.',
    steps: [
      {
        key: 'info',
        title: 'Supplier Info',
        fields: [
          { key: 'name', label: 'Supplier name', type: 'text', required: true, column: true },
          { key: 'owner', label: 'Supplier owner', type: 'member', column: true },
          { key: 'contractEmail', label: 'Supplier contract email address', type: 'email' },
          { key: 'businessCritical', label: 'Business critical', type: 'boolean', column: true },
          { key: 'dpaSigned', label: 'Do you already have a signed DPA in place with this supplier?', type: 'boolean' },
          { key: 'country', label: 'Country', type: 'select', options: COUNTRIES, column: true },
          {
            key: 'services', label: 'Supplier services', type: 'multiselect',
            options: [
              'Accountancy', 'Banking', 'Consultancy', 'Data centre services',
              'Health and safety', 'Health services', 'Human resources',
              'Insurance', 'IT supplier', 'Legal', 'Marketing', 'Medical',
              'Office provision', 'Product development', 'Sales',
              'Security services', 'Software / SaaS', 'Telecoms', 'Training',
              'Utilities', 'Web hosting', 'Other',
            ],
          },
        ],
      },
      {
        key: 'subjects',
        title: 'Subjects',
        fields: [
          {
            key: 'dataSubjects', label: 'Whose data does this supplier handle?', type: 'multiselect',
            options: DATA_SUBJECTS,
          },
          {
            key: 'dataCategories', label: 'Categories of data handled', type: 'multiselect',
            options: DATA_CATEGORIES,
          },
        ],
      },
      {
        key: 'processing',
        title: 'Processing Activities',
        fields: [
          {
            key: 'processingActivities', label: 'Processing performed', type: 'multiselect',
            options: [
              'Hosting & storage', 'Payroll processing', 'Recruitment',
              'Marketing & communications', 'Analytics', 'Customer support',
              'Payment processing', 'Backup & disaster recovery',
              'Development & testing', 'None',
            ],
          },
          {
            key: 'processingLocations', label: 'Where is the data processed?', type: 'multiselect',
            options: ['United Kingdom', 'EEA', 'United States', 'Other third country'],
          },
          { key: 'subProcessors', label: 'Does this supplier use sub-processors?', type: 'boolean' },
          {
            key: 'transferSafeguards', label: 'International transfer safeguards', type: 'select',
            options: TRANSFER_SAFEGUARDS,
          },
        ],
      },
      {
        key: 'impact',
        title: 'Impact Levels',
        fields: [
          { key: 'confidentialityImpact', label: 'Confidentiality impact', type: 'select', options: IMPACT_LEVELS, hint: 'Impact if data held by this supplier were disclosed' },
          { key: 'integrityImpact', label: 'Integrity impact', type: 'select', options: IMPACT_LEVELS },
          { key: 'availabilityImpact', label: 'Availability impact', type: 'select', options: IMPACT_LEVELS },
          { key: 'outageImpact', label: 'Impact of a supplier outage', type: 'textarea', hint: 'What happens to your operations if this supplier is unavailable' },
        ],
      },
      {
        key: 'classification',
        title: 'Classification',
        fields: [
          { key: 'riskRating', label: 'Overall risk rating', type: 'select', options: IMPACT_LEVELS, column: true },
          {
            key: 'reviewFrequency', label: 'Review frequency', type: 'select',
            options: ['Annually', 'Six-monthly', 'Quarterly', 'On contract renewal'],
          },
          { key: 'nextReviewDate', label: 'Next review date', type: 'date' },
          { key: 'contractStart', label: 'Contract start', type: 'date' },
          { key: 'contractEnd', label: 'Contract end', type: 'date' },
          { key: 'notes', label: 'Notes', type: 'textarea' },
        ],
      },
      {
        key: 'cloud',
        title: 'Cloud service & data',
        fields: [
          { key: 'isCloudService', label: 'Cloud service?', type: 'boolean', column: true, hint: 'SaaS/IaaS/PaaS you use, e.g. AWS, Azure, Microsoft 365' },
          { key: 'dataLocationDetail', label: 'Data location detail', type: 'text', hint: 'Regions or data centres, e.g. AWS eu-west-2 (London), Azure UK South' },
          { key: 'accessInternal', label: 'Who has access (internal)?', type: 'text' },
          { key: 'accessExternal', label: 'Who has access (external)?', type: 'text' },
          { key: 'dpia', label: 'DPIA', type: 'record', refCollections: ['dpias'], hint: 'The DPIA (or DPIA screening) covering this service' },
        ],
      },
    ],
  },
  {
    key: 'websites',
    group: 'inventory',
    name: 'Websites',
    singular: 'website',
    description: 'Websites, web applications and public-facing services you operate.',
    steps: [
      {
        key: 'site',
        title: 'Website Info',
        fields: [
          { key: 'name', label: 'Site name', type: 'text', required: true, column: true },
          { key: 'url', label: 'URL', type: 'url', required: true, column: true },
          {
            key: 'purpose', label: 'Purpose', type: 'select', column: true,
            options: ['Marketing', 'Web application', 'API', 'E-commerce', 'Intranet', 'Documentation', 'Other'],
          },
          { key: 'owner', label: 'Site owner', type: 'member', column: true },
          { key: 'publiclyAccessible', label: 'Publicly accessible?', type: 'boolean' },
        ],
      },
      {
        key: 'hosting',
        title: 'Hosting & Security',
        fields: [
          { key: 'host', label: 'Hosting provider', type: 'text', column: true },
          { key: 'registrar', label: 'Domain registrar', type: 'text' },
          { key: 'dnsProvider', label: 'DNS provider', type: 'text' },
          { key: 'tlsEnforced', label: 'TLS / HTTPS enforced?', type: 'boolean', column: true },
          { key: 'certExpiry', label: 'Certificate expiry', type: 'date' },
          { key: 'wafProtected', label: 'WAF / DDoS protection?', type: 'boolean' },
          { key: 'cookieConsent', label: 'Cookie consent banner?', type: 'boolean' },
          {
            key: 'analytics', label: 'Analytics in use', type: 'multiselect',
            options: ['Google Analytics', 'Microsoft Clarity', 'Matomo', 'Plausible', 'None', 'Other'],
          },
          { key: 'privacyPolicyUrl', label: 'Privacy policy URL', type: 'url' },
        ],
      },
    ],
  },

  // ------------------------------------------------------------- registries
  {
    key: 'risks',
    group: 'registry',
    name: 'Risk Registry',
    singular: 'risk',
    description: 'Organisational risks with likelihood, impact and treatment — the risk register ISO 27001 and Cyber Assurance expect.',
    steps: [
      {
        key: 'risk',
        title: 'Risk',
        fields: [
          { key: 'name', label: 'Risk title', type: 'text', required: true, column: true },
          { key: 'description', label: 'Description', type: 'textarea' },
          {
            key: 'category', label: 'Category', type: 'select', column: true,
            options: ['Information security', 'Data protection', 'Operational', 'Financial', 'Legal & regulatory', 'Reputational', 'Strategic', 'Health & safety', 'Supplier / third party'],
          },
          { key: 'owner', label: 'Risk owner', type: 'member', column: true },
          {
            key: 'source', label: 'Identified via', type: 'select',
            options: ['Risk assessment', 'Incident', 'Audit finding', 'Supplier review', 'Management review', 'Other'],
          },
          { key: 'affectedAssets', label: 'Affected assets / processes', type: 'text' },
        ],
      },
      {
        key: 'assessment',
        title: 'Assessment',
        fields: [
          { key: 'likelihood', label: 'Likelihood', type: 'select', options: LIKERT_LIKELIHOOD, column: true },
          { key: 'impact', label: 'Impact', type: 'select', options: LIKERT_IMPACT, column: true },
          { key: 'inherentRating', label: 'Inherent risk rating', type: 'select', options: IMPACT_LEVELS },
        ],
      },
      {
        key: 'treatment',
        title: 'Treatment',
        fields: [
          {
            key: 'treatment', label: 'Treatment decision', type: 'select',
            options: ['Treat (mitigate)', 'Tolerate (accept)', 'Transfer', 'Terminate (avoid)'],
          },
          { key: 'controls', label: 'Existing & planned controls', type: 'textarea' },
          { key: 'annexControls', label: 'ISO 27001 Annex A controls', type: 'text', column: true, hint: 'Controls applied after treatment, e.g. A.8.8 Management of technical vulnerabilities; A.5.7 Threat intelligence' },
          { key: 'residualLikelihood', label: 'Residual likelihood', type: 'select', options: LIKERT_LIKELIHOOD },
          { key: 'residualImpact', label: 'Residual impact', type: 'select', options: LIKERT_IMPACT },
          { key: 'residualRating', label: 'Residual risk rating', type: 'select', options: IMPACT_LEVELS, column: true },
          {
            key: 'state', label: 'Risk status', type: 'select', column: true,
            options: ['Open', 'In treatment', 'Monitored', 'Closed'],
          },
          { key: 'reviewDate', label: 'Next review date', type: 'date' },
        ],
      },
    ],
  },
  {
    key: 'vulnerabilities',
    group: 'registry',
    name: 'Vulnerabilities',
    singular: 'vulnerability',
    description: 'Known vulnerabilities and their remediation against your patching SLAs.',
    steps: [
      {
        key: 'vulnerability',
        title: 'Vulnerability',
        fields: [
          { key: 'name', label: 'Title', type: 'text', required: true, column: true },
          { key: 'reference', label: 'CVE / advisory reference', type: 'text', column: true },
          {
            key: 'severity', label: 'Severity', type: 'select', required: true, column: true,
            options: ['Critical', 'High', 'Medium', 'Low', 'Informational'],
          },
          { key: 'cvss', label: 'CVSS score', type: 'number' },
          { key: 'affected', label: 'Affected systems / software', type: 'textarea' },
          {
            key: 'source', label: 'Discovered via', type: 'select',
            options: ['Vulnerability scan', 'Penetration test', 'Vendor advisory', 'Bug bounty / external report', 'Internal discovery'],
          },
        ],
      },
      {
        key: 'remediation',
        title: 'Remediation',
        fields: [
          { key: 'owner', label: 'Remediation owner', type: 'member', column: true },
          { key: 'discovered', label: 'Date discovered', type: 'date' },
          { key: 'remediationDue', label: 'Remediation due', type: 'date', column: true, hint: 'Per your patching policy SLA (e.g. 14 days for high severity)' },
          { key: 'remediationPlan', label: 'Remediation plan', type: 'textarea' },
          {
            key: 'state', label: 'Status', type: 'select', column: true,
            options: ['Open', 'In progress', 'Mitigated', 'Resolved', 'Risk accepted'],
          },
          { key: 'resolvedDate', label: 'Date resolved', type: 'date' },
        ],
      },
    ],
  },
  {
    key: 'incidents',
    group: 'registry',
    name: 'Incidents',
    singular: 'incident',
    description: 'Security incidents and data breaches, including ICO reportability.',
    steps: [
      {
        key: 'incident',
        title: 'Incident',
        fields: [
          { key: 'name', label: 'Incident title', type: 'text', required: true, column: true },
          {
            key: 'type', label: 'Incident type', type: 'select', column: true,
            options: ['Security incident', 'Data breach', 'Phishing', 'Malware / ransomware', 'Availability / outage', 'Physical security', 'Misdirected data', 'Other'],
          },
          { key: 'severity', label: 'Severity', type: 'select', options: IMPACT_LEVELS, column: true },
          { key: 'occurredAt', label: 'Date occurred', type: 'date', column: true },
          { key: 'detectedAt', label: 'Date detected', type: 'date' },
          { key: 'reportedBy', label: 'Reported by', type: 'text' },
          { key: 'owner', label: 'Incident manager', type: 'member', column: true },
        ],
      },
      {
        key: 'impact',
        title: 'Impact',
        fields: [
          { key: 'description', label: 'What happened', type: 'textarea' },
          { key: 'affectedSystems', label: 'Affected systems', type: 'text' },
          { key: 'personalDataInvolved', label: 'Personal data involved?', type: 'boolean' },
          { key: 'subjectsAffected', label: 'Number of data subjects affected', type: 'number' },
          { key: 'icoReportable', label: 'Reportable to the ICO (within 72 hours)?', type: 'boolean' },
          { key: 'icoReportedAt', label: 'Date reported to ICO', type: 'date' },
          { key: 'subjectsNotified', label: 'Data subjects notified?', type: 'boolean' },
        ],
      },
      {
        key: 'response',
        title: 'Response & Lessons',
        fields: [
          { key: 'containment', label: 'Containment & recovery actions', type: 'textarea' },
          { key: 'rootCause', label: 'Root cause', type: 'textarea' },
          { key: 'lessons', label: 'Lessons learned / follow-up actions', type: 'textarea' },
          {
            key: 'state', label: 'Status', type: 'select', column: true,
            options: ['Open', 'Contained', 'Recovered', 'Closed'],
          },
          { key: 'closedAt', label: 'Date closed', type: 'date' },
        ],
      },
    ],
  },
  {
    key: 'gdpr-requests',
    group: 'registry',
    name: 'GDPR Requests',
    singular: 'request',
    description: 'Data subject rights requests (access, erasure, rectification…) and their statutory deadlines.',
    steps: [
      {
        key: 'request',
        title: 'Request',
        fields: [
          { key: 'name', label: 'Requester name', type: 'text', required: true, column: true },
          {
            key: 'type', label: 'Request type', type: 'select', required: true, column: true,
            options: ['Subject access', 'Erasure', 'Rectification', 'Restriction', 'Portability', 'Objection', 'Consent withdrawal', 'Other'],
          },
          {
            key: 'channel', label: 'Received via', type: 'select',
            options: ['Email', 'Web form', 'Post', 'Phone', 'In person'],
          },
          { key: 'receivedDate', label: 'Date received', type: 'date', required: true, column: true },
          { key: 'dueDate', label: 'Statutory due date', type: 'date', column: true, hint: 'One calendar month from receipt (extendable by two months if complex)' },
          { key: 'identityVerified', label: 'Identity verified?', type: 'boolean' },
          { key: 'owner', label: 'Handled by', type: 'member', column: true },
        ],
      },
      {
        key: 'handling',
        title: 'Handling & Outcome',
        fields: [
          { key: 'scope', label: 'Scope of the request', type: 'textarea' },
          { key: 'systemsSearched', label: 'Systems searched', type: 'text' },
          { key: 'extensionApplied', label: 'Two-month extension applied?', type: 'boolean' },
          {
            key: 'outcome', label: 'Outcome', type: 'select', column: true,
            options: ['Pending', 'Fulfilled', 'Partially fulfilled', 'Refused (exemption)', 'Withdrawn'],
          },
          { key: 'responseDate', label: 'Date responded', type: 'date' },
          { key: 'exemptions', label: 'Exemptions relied on', type: 'text' },
          { key: 'notes', label: 'Notes', type: 'textarea' },
        ],
      },
    ],
  },
  {
    key: 'dpias',
    group: 'registry',
    name: 'DPIAs',
    singular: 'DPIA',
    description: 'Data protection impact assessments for high-risk processing.',
    steps: [
      {
        key: 'screening',
        title: 'Screening',
        fields: [
          { key: 'name', label: 'DPIA title', type: 'text', required: true, column: true },
          { key: 'processing', label: 'Description of the processing', type: 'textarea' },
          { key: 'owner', label: 'DPIA owner', type: 'member', column: true },
          {
            key: 'service', label: 'Cloud service / supplier covered', type: 'record',
            refCollections: ['software', 'suppliers'], column: true,
          },
          { key: 'lawfulBasis', label: 'Lawful basis', type: 'select', options: LAWFUL_BASES },
          {
            key: 'highRiskCriteria', label: 'High-risk criteria met', type: 'multiselect',
            options: [
              'Large-scale special category data', 'Systematic monitoring',
              'Automated decision-making', 'Vulnerable data subjects',
              'New technology', 'Data matching', 'Invisible processing',
              'Cross-border transfer', 'None',
            ],
          },
        ],
      },
      {
        key: 'assessment',
        title: 'Assessment',
        fields: [
          { key: 'necessity', label: 'Necessity & proportionality', type: 'textarea' },
          { key: 'risks', label: 'Risks to data subjects', type: 'textarea' },
          { key: 'measures', label: 'Measures to reduce risk', type: 'textarea' },
          { key: 'residualRisk', label: 'Residual risk', type: 'select', options: ['Low', 'Medium', 'High'], column: true },
          { key: 'dpoConsulted', label: 'DPO consulted?', type: 'boolean' },
          { key: 'icoConsultation', label: 'Prior consultation with the ICO required?', type: 'boolean' },
        ],
      },
      {
        key: 'outcome',
        title: 'Outcome',
        fields: [
          {
            key: 'outcome', label: 'Outcome', type: 'select', column: true,
            options: ['Pending', 'Approved', 'Approved with conditions', 'Rejected'],
          },
          { key: 'approvedBy', label: 'Approved by', type: 'member' },
          { key: 'approvalDate', label: 'Approval date', type: 'date', column: true },
          { key: 'reviewDate', label: 'Next review date', type: 'date' },
        ],
      },
    ],
  },
  {
    key: 'corrective-actions',
    group: 'registry',
    name: 'Corrective Actions',
    singular: 'corrective action',
    description: 'Nonconformities and audit findings with their corrective action plans.',
    steps: [
      {
        key: 'action',
        title: 'Finding',
        fields: [
          { key: 'name', label: 'Title', type: 'text', required: true, column: true },
          {
            key: 'source', label: 'Source', type: 'select', column: true,
            options: ['Internal audit', 'External audit', 'Incident', 'Risk assessment', 'Management review', 'Nonconformity', 'Complaint', 'Other'],
          },
          { key: 'sourceRef', label: 'Source reference', type: 'text' },
          { key: 'description', label: 'Nonconformity / finding', type: 'textarea' },
          { key: 'owner', label: 'Action owner', type: 'member', column: true },
          { key: 'priority', label: 'Priority', type: 'select', options: IMPACT_LEVELS, column: true },
        ],
      },
      {
        key: 'resolution',
        title: 'Resolution',
        fields: [
          { key: 'rootCause', label: 'Root cause', type: 'textarea' },
          { key: 'actionPlan', label: 'Corrective action plan', type: 'textarea' },
          { key: 'dueDate', label: 'Due date', type: 'date', column: true },
          {
            key: 'state', label: 'Status', type: 'select', column: true,
            options: ['Open', 'In progress', 'Implemented', 'Verified effective', 'Closed'],
          },
          { key: 'completedDate', label: 'Date completed', type: 'date' },
          { key: 'verifiedBy', label: 'Effectiveness verified by', type: 'member' },
          { key: 'effectiveness', label: 'Effectiveness check', type: 'textarea' },
        ],
      },
    ],
  },
  {
    key: 'processing',
    group: 'registry',
    name: 'Processing Registry',
    singular: 'processing activity',
    description: 'Your record of processing activities (GDPR Article 30 ROPA).',
    steps: [
      {
        key: 'activity',
        title: 'Activity',
        fields: [
          { key: 'name', label: 'Processing activity', type: 'text', required: true, column: true },
          { key: 'purpose', label: 'Purpose of processing', type: 'textarea' },
          {
            key: 'department', label: 'Department', type: 'select', column: true,
            options: ['HR', 'Finance', 'Sales & marketing', 'Operations', 'IT', 'Customer support', 'Legal', 'Executive'],
          },
          { key: 'owner', label: 'Activity owner', type: 'member', column: true },
          { key: 'lawfulBasis', label: 'Lawful basis', type: 'select', options: LAWFUL_BASES, column: true },
          {
            key: 'specialCategoryCondition', label: 'Special category condition', type: 'select',
            options: [
              'Not applicable', 'Explicit consent',
              'Employment / social security law', 'Vital interests',
              'Legitimate activities (non-profit)', 'Made public by the subject',
              'Legal claims', 'Substantial public interest',
              'Health or social care', 'Public health', 'Archiving / research',
            ],
          },
        ],
      },
      {
        key: 'data',
        title: 'Data & Safeguards',
        fields: [
          { key: 'dataSubjects', label: 'Categories of data subjects', type: 'multiselect', options: DATA_SUBJECTS },
          { key: 'dataCategories', label: 'Categories of personal data', type: 'multiselect', options: DATA_CATEGORIES },
          { key: 'recipients', label: 'Recipients / categories of recipients', type: 'text' },
          { key: 'retention', label: 'Retention period', type: 'text', column: true },
          { key: 'internationalTransfers', label: 'International transfers?', type: 'boolean' },
          { key: 'transferSafeguards', label: 'Transfer safeguards', type: 'select', options: TRANSFER_SAFEGUARDS },
          { key: 'securityMeasures', label: 'Technical & organisational measures', type: 'textarea' },
        ],
      },
    ],
  },
  {
    key: 'change-requests',
    group: 'registry',
    name: 'Change Requests',
    singular: 'change request',
    description: 'Changes to systems and processes with risk assessment and approval.',
    steps: [
      {
        key: 'change',
        title: 'Change',
        fields: [
          { key: 'name', label: 'Change title', type: 'text', required: true, column: true },
          { key: 'type', label: 'Change type', type: 'select', column: true, options: ['Standard', 'Normal', 'Emergency'] },
          {
            key: 'category', label: 'Category', type: 'select',
            options: ['Infrastructure', 'Application', 'Security', 'Process', 'Supplier', 'Other'],
          },
          { key: 'description', label: 'Description', type: 'textarea' },
          { key: 'requestedBy', label: 'Requested by', type: 'member' },
          { key: 'owner', label: 'Change owner', type: 'member', column: true },
          { key: 'priority', label: 'Priority', type: 'select', options: IMPACT_LEVELS, column: true },
        ],
      },
      {
        key: 'assessment',
        title: 'Assessment',
        fields: [
          { key: 'risk', label: 'Change risk', type: 'select', options: ['Low', 'Medium', 'High'], column: true },
          { key: 'impactAssessment', label: 'Impact assessment', type: 'textarea' },
          { key: 'rollbackPlan', label: 'Rollback plan', type: 'textarea' },
          { key: 'testing', label: 'Testing performed', type: 'textarea' },
          { key: 'scheduledDate', label: 'Scheduled date', type: 'date', column: true },
        ],
      },
      {
        key: 'approval',
        title: 'Approval',
        fields: [
          {
            key: 'state', label: 'Status', type: 'select', column: true,
            options: ['Draft', 'Submitted', 'Approved', 'Rejected', 'Implemented', 'Closed'],
          },
          { key: 'approvedBy', label: 'Approved by', type: 'member' },
          { key: 'approvalDate', label: 'Approval date', type: 'date' },
          { key: 'implementedDate', label: 'Date implemented', type: 'date' },
          { key: 'reviewNotes', label: 'Post-implementation review', type: 'textarea' },
        ],
      },
    ],
  },
];

// ------------------------------------------------------------------ helpers

export function getCollection(key: string): CollectionDef | undefined {
  return COLLECTIONS.find((c) => c.key === key);
}

export function collectionFields(def: CollectionDef): CollectionField[] {
  return def.steps.flatMap((s) => s.fields);
}

/** Narrow a field to a record reference (its `refCollections` are then meaningful). */
export function isRecordField(
  field: CollectionField,
): field is CollectionField & { type: 'record' } {
  return field.type === 'record';
}

/** Every collection a def's `record` fields may point at (union, de-duplicated). */
export function refCollectionsOf(def: CollectionDef): string[] {
  const keys = new Set<string>();
  for (const field of collectionFields(def)) {
    if (isRecordField(field)) for (const key of field.refCollections ?? []) keys.add(key);
  }
  return [...keys];
}

/** Columns for list views and snapshot tables (always includes `name`). */
export function collectionColumns(def: CollectionDef): CollectionField[] {
  return collectionFields(def).filter((f) => f.column);
}

export const INVENTORY_COLLECTIONS: readonly CollectionDef[] = COLLECTIONS.filter(
  (c) => c.group === 'inventory',
);
export const REGISTRY_COLLECTIONS: readonly CollectionDef[] = COLLECTIONS.filter(
  (c) => c.group === 'registry',
);
